import { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useAtomValue } from "jotai";
import { useGetSelectedToolboxValue } from "src/components/App/components/AppToolboxSlot/AppToolboxSlot.hooks";
import useBooleanState from "src/hooks/useBooleanState";
import { useClickOutside } from "src/hooks/useClickOutside";
import { captureFeature } from "src/utils/analytics";
import { FAB_SIZE_COMPACT, FAB_SIZE_DEFAULT } from "./FabButton.constants";
import { fabHiddenAtom, fabMenuAtom } from "./FabButton.state";
import { buildRows } from "./FabButton.utils";
import { useBodyMuiDrawerOpen } from "./hooks/useBodyMuiDrawerOpen";

export const useFabButton = () => {
  const toolboxValue = useGetSelectedToolboxValue();
  const isDrawerOpen = useBodyMuiDrawerOpen();
  const { sections: pageSections, hidden } = useAtomValue(fabMenuAtom);
  const fabHidden = useAtomValue(fabHiddenAtom);
  const { visible: open, hide: close, toggle } = useBooleanState();
  const {
    visible: feedbackOpen,
    show: openFeedback,
    hide: closeFeedback,
  } = useBooleanState();

  const handleClickOutside = useCallback(() => close(), [close]);
  const handleOnMouseDownCapture = useClickOutside(handleClickOutside);
  useHotkeys("esc", close, { enabled: open });

  const isToolboxOpen = toolboxValue != null && toolboxValue !== "";
  const isCompact = isToolboxOpen || isDrawerOpen;
  const fabSize = isCompact ? FAB_SIZE_COMPACT : FAB_SIZE_DEFAULT;
  const isHidden = hidden || fabHidden;

  const handleFeedbackClick = () => {
    close();
    captureFeature("feedback_open");
    openFeedback();
  };

  // TODO(tours): point onTriggerClick at toggleMenu once product tours ship.
  const toggleMenu = () => {
    if (!open) {
      captureFeature("fab_menu_open");
    }
    toggle();
  };
  const onTriggerClick = handleFeedbackClick;

  // Skip row building while hidden (FabButton returns null) - matches the
  // pre-refactor order where row assembly ran after the early return.
  const rows = isHidden
    ? []
    : buildRows({ pageSections, open, onFeedbackClick: handleFeedbackClick });

  return {
    isHidden,
    open,
    isCompact,
    fabSize,
    rows,
    feedbackOpen,
    closeFeedback,
    onTriggerClick,
    toggleMenu,
    handleOnMouseDownCapture,
  };
};
